import { components } from "@/api-schema";
import {
  useRelocationOrderPickupLocationsQuery,
  type PickupLocationsBody,
  type PickupLocationsResponse,
} from "@/common/api/relocation-order/use-relocation-order-pickup-locations.query";
import { useUserLocation } from "@/common/hooks/use-user-location";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { UseQueryOptions } from "@tanstack/react-query";
import { AlertCircle, LocateFixedIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PickupLocation, PickupLocationType, SearchableLocationSelector } from "./searchable-location-selector";

type Branch = components["schemas"]["Branch"];
type AddressCityGroup = components["schemas"]["AddressCityGroup"];
type NearbyLocationsQueryFn = (
  body?: PickupLocationsBody,
  queryOptions?: Omit<
    UseQueryOptions<PickupLocationsResponse, Error, PickupLocationsResponse, readonly unknown[]>,
    "queryKey" | "queryFn"
  >,
) => ReturnType<typeof useRelocationOrderPickupLocationsQuery>;

interface GeoLocationSelectorProps {
  pickupBranches: Branch[];
  addressGroups: AddressCityGroup[];
  selectedBranch: string | null;
  selectedCity: string | null;
  onLocationChange: (location: PickupLocation | null) => void;
  isLoading?: boolean;
  className?: string;
  useNearbyLocationsQuery?: NearbyLocationsQueryFn;
}

/**
 * Location selector with geolocation support.
 * Finds branches and external addresses within 1km radius and auto-selects nearest one.
 */
export function GeoLocationSelector({
  pickupBranches,
  addressGroups,
  selectedBranch,
  selectedCity,
  onLocationChange,
  isLoading = false,
  className,
  useNearbyLocationsQuery = useRelocationOrderPickupLocationsQuery,
}: GeoLocationSelectorProps) {
  const { t } = useTranslation(["fit"]);
  const { userLocation, locationError, getUserLocation, isGettingLocation, clearUserLocation } = useUserLocation();
  const [manuallyCleared, setManuallyCleared] = useState(false);

  const nearbyQuery = useNearbyLocationsQuery(
    userLocation
      ? {
          centerLatitude: userLocation.latitude,
          centerLongitude: userLocation.longitude,
          radiusKm: 1,
        }
      : undefined,
    { enabled: !!userLocation },
  );

  const nearbyBranches = useMemo(() => {
    if (!userLocation) return [];

    return nearbyQuery.data?.branches ?? [];
  }, [userLocation, nearbyQuery.data?.branches]);

  const nearbyAddressGroups = useMemo(() => {
    if (!userLocation) return [];

    return nearbyQuery.data?.addressGroups ?? [];
  }, [userLocation, nearbyQuery.data?.addressGroups]);

  const hasNoLocationNearby =
    userLocation &&
    !selectedBranch &&
    !selectedCity &&
    !nearbyQuery.isPending &&
    !isLoading &&
    nearbyBranches.length === 0 &&
    nearbyAddressGroups.length === 0;

  const isLocationBasedSelection =
    !manuallyCleared &&
    userLocation &&
    !nearbyQuery.isPending &&
    !isLoading &&
    (nearbyBranches.length > 0 || nearbyAddressGroups.length > 0);

  const handleSelectionChange = useCallback(
    (location: PickupLocation) => {
      onLocationChange(location);
      setManuallyCleared(true);
      clearUserLocation();
    },
    [onLocationChange, clearUserLocation],
  );

  const handleClear = useCallback(() => {
    clearUserLocation();
    onLocationChange(null);
    setManuallyCleared(true);
  }, [clearUserLocation, onLocationChange]);

  const handleGetUserLocation = useCallback(() => {
    setManuallyCleared(false);
    getUserLocation();
  }, [getUserLocation]);

  const onLocationChangeRef = useRef(onLocationChange);
  useLayoutEffect(() => {
    onLocationChangeRef.current = onLocationChange;
  });

  const appliedUserLocationRef = useRef<typeof userLocation>(null);

  useEffect(() => {
    if (!userLocation || nearbyQuery.isPending || nearbyQuery.isFetching || isLoading || manuallyCleared) return;
    if (appliedUserLocationRef.current === userLocation) return;
    appliedUserLocationRef.current = userLocation;

    if (nearbyBranches.length > 0) {
      onLocationChangeRef.current({ type: PickupLocationType.Branch, uuid: nearbyBranches[0].uuid });
    } else if (nearbyAddressGroups.length > 0) {
      onLocationChangeRef.current({ type: PickupLocationType.City, city: nearbyAddressGroups[0].city });
    } else {
      onLocationChangeRef.current(null);
    }
  }, [
    userLocation,
    nearbyBranches,
    nearbyAddressGroups,
    nearbyQuery.isPending,
    nearbyQuery.isFetching,
    isLoading,
    manuallyCleared,
  ]);

  const isClearDisabled = !userLocation && !selectedBranch && !selectedCity;

  return (
    <div className={className}>
      {locationError && (
        <Alert variant={ButtonColorVariant.Error} className="mb-4 py-2">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">{locationError}</AlertDescription>
        </Alert>
      )}
      {hasNoLocationNearby && (
        <Alert variant={ButtonColorVariant.Error} className="mb-4 py-2">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">{t("fit:NoLocationNearbyPleaseSelectManually")}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-stretch gap-2 sm:gap-3">
        <Button
          onClick={handleGetUserLocation}
          disabled={isGettingLocation || (!!userLocation && nearbyQuery.isPending) || !!isLocationBasedSelection}
        >
          {isGettingLocation || (!!userLocation && nearbyQuery.isPending) ? (
            <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />
          ) : (
            <LocateFixedIcon />
          )}
        </Button>

        <SearchableLocationSelector
          pickupBranches={pickupBranches}
          addressGroups={addressGroups}
          selectedBranch={selectedBranch}
          selectedCity={selectedCity}
          onLocationChange={handleSelectionChange}
          isLoading={isLoading}
        />

        <Button
          onClick={handleClear}
          variant={ButtonVariant.Outlined}
          colorVariant={ButtonColorVariant.Secondary}
          disabled={isClearDisabled}
        >
          <XIcon />
        </Button>
      </div>
    </div>
  );
}
